import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, siteUrl } from "@/lib/seo";

const LAST_UPDATED = "12 mei 2026";

const Privacy = () => {
  const { lang } = useLang();

  useSEO({
    title: lang === "nl"
      ? "Privacyverklaring — Wasz.nl"
      : "Privacy Policy — Wasz.nl",
    description: lang === "nl"
      ? "Hoe Wasz.nl persoonsgegevens verwerkt: contactformulieren, e-mailverkeer en website-analytics. AVG-conform en transparant."
      : "How Wasz.nl handles personal data: contact forms, e-mail traffic and website analytics. GDPR-compliant and transparent.",
    canonical: siteUrl("/privacy"),
    lang,
    jsonLd: [
      {
        id: "privacy-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Privacyverklaring" : "Privacy Policy", url: siteUrl("/privacy") },
        ]),
      },
    ],
  });

  const Section = ({ id, title, children }) => (
    <section id={id} className="scroll-mt-28 mb-12">
      <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-4">{title}</h2>
      <div className="text-white/70 text-sm sm:text-[15px] leading-relaxed space-y-3">{children}</div>
    </section>
  );

  return (
    <main data-testid="privacy-page" className="min-h-screen pt-20 pb-24">
      <section className="mx-auto max-w-4xl px-6 lg:px-8">
        <div className="mb-12">
          <p className="section-overline mb-3 inline-flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> {lang === "nl" ? "Juridisch" : "Legal"}
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[1.05]">
            {lang === "nl" ? "Privacyverklaring" : "Privacy Policy"}
          </h1>
          <p className="mt-4 text-white/55 text-sm">
            {lang === "nl" ? "Laatst bijgewerkt: " : "Last updated: "}
            <span className="text-white/80 font-medium">{LAST_UPDATED}</span>
          </p>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed">
            {lang === "nl"
              ? "Wasz.nl hecht groot belang aan een zorgvuldige omgang met uw persoonsgegevens. In deze verklaring leggen wij uit welke gegevens wij verwerken, waarom, hoe lang, en welke rechten u heeft onder de Algemene Verordening Gegevensbescherming (AVG)."
              : "Wasz.nl takes the careful handling of your personal data seriously. This policy explains which data we process, why, for how long, and what rights you have under the General Data Protection Regulation (GDPR)."}
          </p>
        </div>

        {/* TOC */}
        <nav
          aria-label={lang === "nl" ? "Inhoud" : "Contents"}
          className="rounded-2xl border border-white/10 bg-[#0F0F23] p-6 mb-14"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#00D4FF] font-semibold mb-3">
            {lang === "nl" ? "Inhoud" : "Contents"}
          </p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/70 list-decimal list-inside marker:text-white/30">
            <li><a href="#verwerker" className="hover:text-[#00D4FF]">{lang === "nl" ? "Verwerkingsverantwoordelijke" : "Data controller"}</a></li>
            <li><a href="#gegevens" className="hover:text-[#00D4FF]">{lang === "nl" ? "Welke gegevens" : "Which data"}</a></li>
            <li><a href="#doelen" className="hover:text-[#00D4FF]">{lang === "nl" ? "Doelen & grondslag" : "Purposes & legal basis"}</a></li>
            <li><a href="#bewaartermijn" className="hover:text-[#00D4FF]">{lang === "nl" ? "Bewaartermijnen" : "Retention periods"}</a></li>
            <li><a href="#derden" className="hover:text-[#00D4FF]">{lang === "nl" ? "Verstrekking aan derden" : "Sharing with third parties"}</a></li>
            <li><a href="#beveiliging" className="hover:text-[#00D4FF]">{lang === "nl" ? "Beveiliging" : "Security"}</a></li>
            <li><a href="#rechten" className="hover:text-[#00D4FF]">{lang === "nl" ? "Uw rechten" : "Your rights"}</a></li>
            <li><a href="#klachten" className="hover:text-[#00D4FF]">{lang === "nl" ? "Klachten & AP" : "Complaints & DPA"}</a></li>
            <li><a href="#wijzigingen" className="hover:text-[#00D4FF]">{lang === "nl" ? "Wijzigingen" : "Changes"}</a></li>
            <li><a href="#contact" className="hover:text-[#00D4FF]">{lang === "nl" ? "Contact" : "Contact"}</a></li>
          </ol>
        </nav>

        {lang === "nl" ? (
          <>
            <Section id="verwerker" title="1. Verwerkingsverantwoordelijke">
              <p>
                Wasz.nl is de verantwoordelijke voor de verwerking van uw persoonsgegevens.
                U kunt ons bereiken via:
              </p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Postadres: Scharstraat 25, 1317 PL Almere</li>
                <li>E-mail: <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a></li>
                <li>Telefoon: <a className="text-[#00D4FF] hover:underline" href="tel:0365255021">036 525 5021</a></li>
              </ul>
            </Section>

            <Section id="gegevens" title="2. Welke gegevens verwerken wij">
              <p>Afhankelijk van het contactmoment verwerken wij:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li><strong className="text-white">Contactformulier / offerte / demo-aanvraag:</strong> naam, bedrijfsnaam, e-mailadres, telefoonnummer, eventuele wagenparkomvang, en het bericht dat u zelf invult.</li>
                <li><strong className="text-white">E-mail- en telefonisch verkeer:</strong> de inhoud van uw bericht of gesprek en uw contactgegevens.</li>
                <li><strong className="text-white">Klantadministratie:</strong> indien u klant wordt, leggen wij naast bovenstaande gegevens ook factuur-, betaal- en wagenparkgegevens vast (kentekens, locaties, kostenplaatsen).</li>
                <li><strong className="text-white">Website-analytics:</strong> uitsluitend nadat u via de cookiebanner toestemming geeft, verzamelt Google Analytics geanonimiseerde gebruiksgegevens (zie onze <Link to="/cookies" className="text-[#00D4FF] hover:underline">Cookieverklaring</Link>).</li>
              </ul>
            </Section>

            <Section id="doelen" title="3. Doelen & juridische grondslag">
              <p>Wij gebruiken uw gegevens uitsluitend voor:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Het beantwoorden van uw aanvraag en het opstellen van een offerte (<em>uitvoering overeenkomst / pre-contractuele maatregelen</em>).</li>
                <li>Het uitvoeren van overeengekomen reinigingswerkzaamheden en aanverwante administratie (<em>uitvoering overeenkomst</em>).</li>
                <li>Het voldoen aan wettelijke verplichtingen, waaronder de bewaarplicht van administratie en HACCP-/NVWA-documentatie (<em>wettelijke verplichting</em>).</li>
                <li>Het verbeteren van onze website door anonieme statistieken (<em>toestemming</em>).</li>
              </ul>
            </Section>

            <Section id="bewaartermijn" title="4. Bewaartermijnen">
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Aanvragen via het contactformulier waar geen overeenkomst uit voortkomt: maximaal <strong className="text-white">12 maanden</strong>.</li>
                <li>Klant- en factuurgegevens: <strong className="text-white">7 jaar</strong> conform de wettelijke fiscale bewaarplicht.</li>
                <li>HACCP- en reinigingscertificaten: minimaal <strong className="text-white">2 jaar</strong>, conform de levensmiddelenwetgeving.</li>
                <li>Analytics-gegevens (Google Analytics 4): conform de standaardinstelling van GA4 (maximaal 14 maanden).</li>
              </ul>
            </Section>

            <Section id="derden" title="5. Verstrekking aan derden">
              <p>Wij verkopen uw gegevens nooit. Wij delen alleen gegevens met onderstaande verwerkers, uitsluitend voor zover noodzakelijk:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li><strong className="text-white">FormSubmit.co</strong> — doorgifte van contactformulier-inhoud naar info@wasz.nl.</li>
                <li><strong className="text-white">Google (Google Analytics 4)</strong> — geanonimiseerde gebruiksstatistieken, alleen na uw toestemming.</li>
                <li><strong className="text-white">Onze hostingpartner</strong> — voor het draaien van de website en het opslaan van formulier-aanvragen in onze database.</li>
                <li><strong className="text-white">Boekhouder en fiscus</strong> — uitsluitend voor klantfacturatie en wettelijke verplichtingen.</li>
              </ul>
              <p>Met al onze verwerkers hebben wij verwerkersovereenkomsten of equivalente afspraken gesloten conform artikel 28 AVG.</p>
            </Section>

            <Section id="beveiliging" title="6. Beveiliging">
              <p>
                Wij nemen passende technische en organisatorische maatregelen om uw persoonsgegevens te
                beschermen tegen verlies of onrechtmatig gebruik, waaronder TLS-versleuteling van alle
                formulier-inzendingen, beperkte toegang op need-to-know-basis en periodieke evaluatie
                van onze beveiligingsmaatregelen.
              </p>
            </Section>

            <Section id="rechten" title="7. Uw rechten">
              <p>U heeft te allen tijde recht op:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Inzage in de persoonsgegevens die wij van u verwerken.</li>
                <li>Rectificatie van onjuiste of onvolledige gegevens.</li>
                <li>Verwijdering ("vergetelheid"), voor zover wettelijk toegestaan.</li>
                <li>Beperking van de verwerking.</li>
                <li>Bezwaar tegen verwerking.</li>
                <li>Overdraagbaarheid van uw gegevens.</li>
                <li>Het intrekken van eerder gegeven toestemming (bijvoorbeeld voor analytics).</li>
              </ul>
              <p>
                Een verzoek kunt u indienen via <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a>.
                Wij reageren binnen 4 weken.
              </p>
            </Section>

            <Section id="klachten" title="8. Klachten & Autoriteit Persoonsgegevens">
              <p>
                Bent u niet tevreden over hoe wij met uw gegevens omgaan? Wij horen het graag zodat
                wij dit kunnen oplossen. U heeft daarnaast altijd het recht een klacht in te dienen
                bij de Autoriteit Persoonsgegevens via{" "}
                <a className="text-[#00D4FF] hover:underline" href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">
                  autoriteitpersoonsgegevens.nl
                </a>.
              </p>
            </Section>

            <Section id="wijzigingen" title="9. Wijzigingen">
              <p>
                Wij kunnen deze verklaring aanpassen wanneer wet- of regelgeving of onze
                dienstverlening daartoe aanleiding geeft. De meest actuele versie staat altijd op
                deze pagina, voorzien van een datum van bijwerking.
              </p>
            </Section>

            <Section id="contact" title="10. Contact">
              <p>
                Heeft u vragen over deze verklaring? Neem gerust contact op via{" "}
                <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a>{" "}
                of bel <a className="text-[#00D4FF] hover:underline" href="tel:0365255021">036 525 5021</a>.
              </p>
            </Section>
          </>
        ) : (
          <>
            <Section id="verwerker" title="1. Data controller">
              <p>Wasz.nl is the data controller responsible for processing your personal data. You can reach us at:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Postal address: Scharstraat 25, 1317 PL Almere, the Netherlands</li>
                <li>E-mail: <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a></li>
                <li>Phone: <a className="text-[#00D4FF] hover:underline" href="tel:+31365255021">+31 36 525 5021</a></li>
              </ul>
            </Section>
            <Section id="gegevens" title="2. Which data we process">
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li><strong className="text-white">Contact / quote / demo form:</strong> name, company, e-mail, phone, optional fleet size and your message.</li>
                <li><strong className="text-white">E-mail and phone contact:</strong> the contents of your message and your contact details.</li>
                <li><strong className="text-white">Customer records:</strong> if you become a customer, we also process invoicing and fleet data (license plates, locations, cost centres).</li>
                <li><strong className="text-white">Website analytics:</strong> only after consent via the cookie banner, Google Analytics collects anonymised usage data (see our <Link to="/cookies" className="text-[#00D4FF] hover:underline">Cookie Policy</Link>).</li>
              </ul>
            </Section>
            <Section id="doelen" title="3. Purposes & legal basis">
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Responding to your request and providing quotes (<em>performance of a contract / pre-contractual measures</em>).</li>
                <li>Delivering the agreed cleaning services and related administration (<em>performance of a contract</em>).</li>
                <li>Complying with legal obligations including tax retention and HACCP/NVWA documentation (<em>legal obligation</em>).</li>
                <li>Improving our website through anonymous statistics (<em>consent</em>).</li>
              </ul>
            </Section>
            <Section id="bewaartermijn" title="4. Retention periods">
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Form requests not leading to a contract: max. <strong className="text-white">12 months</strong>.</li>
                <li>Customer and invoicing data: <strong className="text-white">7 years</strong>, per Dutch tax law.</li>
                <li>HACCP & cleaning certificates: minimum <strong className="text-white">2 years</strong>, per food-safety law.</li>
                <li>Analytics data (GA4): per the GA4 default (max. 14 months).</li>
              </ul>
            </Section>
            <Section id="derden" title="5. Sharing with third parties">
              <p>We never sell your data. We share data only with the following processors, strictly as needed:</p>
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li><strong className="text-white">FormSubmit.co</strong> — forwarding contact-form content to info@wasz.nl.</li>
                <li><strong className="text-white">Google (Google Analytics 4)</strong> — anonymised usage statistics, only after consent.</li>
                <li><strong className="text-white">Our hosting provider</strong> — to run the website and store form submissions in our database.</li>
                <li><strong className="text-white">Accountant and tax authority</strong> — for customer invoicing and legal obligations.</li>
              </ul>
              <p>We have data-processing agreements in place with all processors pursuant to article 28 GDPR.</p>
            </Section>
            <Section id="beveiliging" title="6. Security">
              <p>We apply appropriate technical and organisational measures to protect your personal data, including TLS encryption of all form submissions, need-to-know access control, and regular security reviews.</p>
            </Section>
            <Section id="rechten" title="7. Your rights">
              <ul className="list-disc list-inside space-y-1 marker:text-white/30">
                <li>Access to the personal data we process about you.</li>
                <li>Rectification of inaccurate or incomplete data.</li>
                <li>Erasure ("right to be forgotten"), where legally permitted.</li>
                <li>Restriction of processing.</li>
                <li>Objection to processing.</li>
                <li>Data portability.</li>
                <li>Withdrawal of previously given consent (e.g. analytics).</li>
              </ul>
              <p>Requests can be sent to <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a>. We respond within 4 weeks.</p>
            </Section>
            <Section id="klachten" title="8. Complaints & supervisory authority">
              <p>If you are not satisfied with how we handle your data, please let us know first so we can resolve it. You also have the right to file a complaint with the Dutch Data Protection Authority via <a className="text-[#00D4FF] hover:underline" href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">autoriteitpersoonsgegevens.nl</a>.</p>
            </Section>
            <Section id="wijzigingen" title="9. Changes">
              <p>We may revise this policy as laws or our services evolve. The latest version is always on this page, with the date of last update.</p>
            </Section>
            <Section id="contact" title="10. Contact">
              <p>Questions about this policy? Reach out via <a className="text-[#00D4FF] hover:underline" href="mailto:info@wasz.nl">info@wasz.nl</a> or call <a className="text-[#00D4FF] hover:underline" href="tel:+31365255021">+31 36 525 5021</a>.</p>
            </Section>
          </>
        )}

        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between text-sm">
          <Link to="/cookies" className="inline-flex items-center gap-2 text-white/70 hover:text-[#00D4FF]" data-testid="link-to-cookies">
            {lang === "nl" ? "Lees onze Cookieverklaring" : "Read our Cookie Policy"} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="text-white/50 hover:text-[#00D4FF]">
            {lang === "nl" ? "Contact" : "Contact"}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Privacy;
